import { ref, type Ref } from 'vue'
import { hasNativeFiles } from '~~/shared/documentDrop'

export interface ExplorerDropDeps {
  isFolderMode: Ref<boolean>
  /** Set while a Marcus row is being dragged — what makes a drop an internal MOVE. */
  dragItemId: Ref<string | null>
  currentPath: Ref<string>
  dropOnto: (destination: string) => Promise<void>
  /** Ask where OS files belong, then upload them (the #83 target picker). */
  requestUpload: (files: File[], opts: { path: string }) => unknown
}

/**
 * The documents sidebar's drop targets: its breadcrumb and its list background (MAR-948 — split
 * out of `FileExplorer.vue`). Two kinds of drop land here:
 *
 * - **a Marcus row** — an internal MOVE into the crumb's path or the folder on screen. Folder/path
 *   moves only mean something in the folder listing, so grouped views ignore them.
 * - **OS files** on the list background (#83). Explicit, because it used to be implicit: the
 *   window-level catch-all in `PanelLayout.vue` caught every native drop in the app. That is gone,
 *   so the drop this surface is *supposed* to have is its own — the browser is open at a place, and
 *   dropping into it means "put it here". It goes through the same ask-then-upload path
 *   `useFolderDrop` uses; a drop on a folder ROW is handled there and stops propagation, so the two
 *   never both fire.
 */
export function useExplorerDrop({ isFolderMode, dragItemId, currentPath, dropOnto, requestUpload }: ExplorerDropDeps) {
  const breadcrumbDropTarget = ref<string | null>(null)
  const listDropActive = ref(false)

  function onInternalDragOver(e: DragEvent, target: { breadcrumb?: string; list?: boolean }) {
    if (!isFolderMode.value || !dragItemId.value) return
    e.preventDefault()
    e.dataTransfer!.dropEffect = 'move'
    if (target.breadcrumb !== undefined) breadcrumbDropTarget.value = target.breadcrumb
    if (target.list) listDropActive.value = true
  }

  function onInternalDrop(e: DragEvent, destination: string) {
    if (!isFolderMode.value) return
    e.preventDefault()
    breadcrumbDropTarget.value = null
    listDropActive.value = false
    // Skip drops onto the folder the items already live in (a no-op move).
    if (destination === currentPath.value) return
    void dropOnto(destination)
  }

  function onFilesDragOver(e: DragEvent) {
    if (!hasNativeFiles(e.dataTransfer?.types)) return
    e.preventDefault()
    e.dataTransfer!.dropEffect = 'copy'
    listDropActive.value = true
  }

  function onFilesDrop(e: DragEvent): void {
    listDropActive.value = false
    const files = Array.from(e.dataTransfer?.files ?? [])
    if (!files.length) return
    e.preventDefault()
    e.stopPropagation()
    // Hand the RAW list over rather than pre-filtering here: `requestUpload` partitions it itself
    // and names whatever it rejects in the dialog (#804). Filtering to `valid` first meant a `.zip`
    // mixed in with real documents lost its name before the store ever saw it.
    void requestUpload(files, { path: isFolderMode.value ? currentPath.value : '/' })
  }

  /** A dragleave that only crossed into a child is not a leave. */
  function onContainerDragLeave(e: DragEvent, clear: () => void) {
    const el = e.currentTarget as HTMLElement
    if (el.contains(e.relatedTarget as Node)) return
    clear()
  }

  return {
    breadcrumbDropTarget, listDropActive,
    onInternalDragOver, onInternalDrop, onFilesDragOver, onFilesDrop, onContainerDragLeave,
  }
}
